"use client";

import { useMemo, useState } from "react";
import {
  fillTemplate,
  missingRequired,
  parsePlaceholders,
  type Placeholder,
} from "@/lib/placeholders";
import type { Json } from "@/lib/database.types";

export type CardViewData = {
  id: string;
  number: number;
  name: string;
  purpose: string | null;
  command: string;
  shell: string | null;
  destructive: boolean;
  privileged: boolean;
  placeholders: Json;
  warnings: string | null;
  prerequisites?: string | null;
  working_directory?: string | null;
  expected_output?: string | null;
  success_criteria?: string | null;
  last_tested_on?: string | null;
  tested_versions?: string | null;
  relatedRules?: { number: number; statement: string }[];
};

/**
 * Reader-side command card: fill placeholders, copy the completed command.
 * Values are used for the copy and forgotten — nothing typed here is ever
 * persisted (Workshop principle 6); secret-kind fields are masked.
 * Destructive/privileged cards demand an explicit confirmation step (W2).
 */
export function CommandCardView({
  card,
  bookSlug,
  compact = false,
}: {
  card: CardViewData;
  bookSlug?: string;
  compact?: boolean;
}) {
  const placeholders = useMemo<Placeholder[]>(() => parsePlaceholders(card.placeholders), [card.placeholders]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState(false);
  const [copied, setCopied] = useState(false);

  const filled = fillTemplate(card.command, values);
  const missing = missingRequired(card.command, placeholders, values);
  const needsConfirm = card.destructive || card.privileged;

  async function copy() {
    if (needsConfirm && !confirming) {
      setConfirming(true);
      return;
    }
    setConfirming(false);
    try {
      await navigator.clipboard.writeText(filled);
    } catch {
      // Clipboard API rejects on focus loss; fall back to execCommand.
      const ta = document.createElement("textarea");
      ta.value = filled;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  }

  return (
    <div className="rounded-[12px] border border-border bg-surface p-4">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-2">
        <span
          className="rounded-[6px] px-1.5 py-0.5 font-mono text-[11px] font-bold"
          style={{ background: "var(--surface-alt2)", color: "var(--copper)" }}
        >
          ⌘{card.number}
        </span>
        <span className="min-w-0 flex-1 truncate font-serif text-[15.5px] font-semibold text-ink">{card.name}</span>
        {card.shell && (
          <span className="rounded-full border border-border-soft px-2 py-0.5 font-mono text-[10.5px] text-muted">
            {card.shell}
          </span>
        )}
        {card.destructive && <DangerBadge label="Destructive" />}
        {card.privileged && <DangerBadge label="Privileged" />}
      </div>
      {card.purpose && !compact && <p className="mt-1.5 text-[13px] leading-relaxed text-ink-3">{card.purpose}</p>}

      {/* Template */}
      <pre className="mt-2.5 overflow-x-auto rounded-[8px] border border-border-soft bg-paper/60 px-3 py-2.5 font-mono text-[12.5px] leading-relaxed text-ink-2">
        {highlightPlaceholders(filled)}
      </pre>

      {/* Fill */}
      {placeholders.length > 0 && (
        <div className="mt-2.5 grid gap-1.5 sm:grid-cols-2">
          {placeholders.map((p) => (
            <label key={p.name} className="flex min-w-0 items-center gap-2">
              <code className="shrink-0 font-mono text-[11.5px] font-bold text-copper">{p.name}</code>
              <input
                type={p.kind === "secret" ? "password" : "text"}
                value={values[p.name] ?? ""}
                onChange={(e) => setValues({ ...values, [p.name]: e.target.value })}
                placeholder={p.kind === "secret" ? "filled at use, never stored" : p.hint || p.example || p.label}
                autoComplete="off"
                className="min-w-0 flex-1 rounded-[6px] border border-border-soft bg-surface px-2 py-1.5 text-[12.5px] text-ink-2 outline-none focus:outline-2 focus:outline-copper"
              />
            </label>
          ))}
        </div>
      )}

      {/* Warnings */}
      {card.warnings && !compact && (
        <p className="mt-2 rounded-[8px] border border-border-soft bg-paper/50 px-3 py-2 text-[12.5px] leading-relaxed text-ink-3">
          <strong className="text-copper">Careful:</strong> {card.warnings}
        </p>
      )}

      {/* Copy row */}
      <div className="mt-2.5 flex flex-wrap items-center gap-2.5">
        {confirming ? (
          <>
            <span className="text-[12.5px] font-semibold" style={{ color: "#a4442c" }}>
              This command is {card.destructive ? "destructive" : "privileged"} — copy anyway?
            </span>
            <button
              onClick={copy}
              className="rounded-[8px] px-3 py-1.5 text-[12.5px] font-bold text-surface"
              style={{ background: "#a4442c" }}
            >
              Copy it
            </button>
            <button
              onClick={() => setConfirming(false)}
              className="rounded-[8px] border border-border px-3 py-1.5 text-[12.5px] font-semibold text-muted hover:border-copper hover:text-copper"
            >
              Cancel
            </button>
          </>
        ) : (
          <button
            onClick={copy}
            disabled={missing.length > 0}
            title={missing.length > 0 ? `Fill ${missing.map((n) => `{{${n}}}`).join(", ")} first` : "Copy the completed command"}
            className="rounded-[8px] bg-green px-3.5 py-1.5 text-[12.5px] font-bold text-surface disabled:opacity-40"
          >
            {copied ? "Copied ✓" : "Copy command"}
          </button>
        )}
        {missing.length > 0 && !confirming && (
          <span className="text-[12px] text-muted">
            fill {missing.map((n) => `{{${n}}}`).join(", ")} to copy
          </span>
        )}
        {(card.last_tested_on || card.tested_versions) && !compact && (
          <span className="ml-auto text-[11.5px] text-muted-2">
            {card.last_tested_on ? `Last tested ${card.last_tested_on}` : ""}
            {card.last_tested_on && card.tested_versions ? " · " : ""}
            {card.tested_versions ?? ""}
          </span>
        )}
      </div>

      {/* Context (full view only) */}
      {!compact && (card.prerequisites || card.working_directory || card.expected_output || card.success_criteria) && (
        <dl className="mt-3 grid gap-x-5 gap-y-1.5 border-t border-border-soft pt-2.5 text-[12.5px] sm:grid-cols-2">
          {card.prerequisites && <ContextRow label="Prerequisites" value={card.prerequisites} />}
          {card.working_directory && <ContextRow label="Run from" value={card.working_directory} mono />}
          {card.expected_output && <ContextRow label="Expected output" value={card.expected_output} />}
          {card.success_criteria && <ContextRow label="Success looks like" value={card.success_criteria} />}
        </dl>
      )}

      {/* Related rules */}
      {!compact && bookSlug && (card.relatedRules?.length ?? 0) > 0 && (
        <p className="mt-2.5 text-[12.5px] text-muted">
          Follows{" "}
          {card.relatedRules!.map((r, i) => (
            <span key={r.number}>
              {i > 0 && ", "}
              <a href={`/books/${bookSlug}/rules/${r.number}`} className="font-semibold text-green hover:text-copper">
                Rule {r.number}
              </a>
            </span>
          ))}
        </p>
      )}
    </div>
  );
}

function highlightPlaceholders(text: string): React.ReactNode {
  const parts = text.split(/(\{\{[a-z][a-z0-9_]*\}\})/g);
  return parts.map((part, i) =>
    /^\{\{[a-z][a-z0-9_]*\}\}$/.test(part) ? (
      <span key={i} className="font-bold text-copper">{part}</span>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

function DangerBadge({ label }: { label: string }) {
  return (
    <span
      className="rounded-full px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[.06em]"
      style={{ background: "#a4442c18", color: "#a4442c" }}
    >
      ⚠ {label}
    </span>
  );
}

function ContextRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex gap-2">
      <dt className="shrink-0 font-bold text-muted-3">{label}:</dt>
      <dd className={`min-w-0 text-ink-3 ${mono ? "font-mono text-[12px]" : ""}`}>{value}</dd>
    </div>
  );
}
